/**
 * Dawn Breadcrumbs JavaScript
 * Context-sensitive breadcrumb navigation with sessionStorage tracking
 * Supports Men/Women/Unisex entry points without collection URLs
 */

class BreadcrumbManager {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  async setup() {
    // Support both section and inline breadcrumbs
    const nav = document.querySelector('[id^="breadcrumb-nav-"], #breadcrumb-nav-inline');
    if (!nav) return;

    this.nav = nav;
    this.listEl = nav.querySelector('[id^="breadcrumb-list-"], #breadcrumb-list-inline');
    this.sectionId = nav.querySelector('script[data-section-id]')?.dataset.sectionId;
    this.separator = nav.querySelector('script[data-separator]')?.dataset.separator || '›';
    
    // Get labels and URLs from data attributes
    this.config = {
      homeLabel: nav.dataset.bcHomeLabel || 'Home',
      allLabel: nav.dataset.bcAllLabel || 'All Products',
      shopLabel: nav.dataset.bcShopLabel || 'Shop',
      homeUrl: nav.dataset.bcHomeUrl || '/',
      allUrl: nav.dataset.bcAllUrl || '/collections/all',
      productTitle: nav.dataset.bcProductTitle,
      productUrl: nav.dataset.bcProductUrl,
      productCollections: nav.dataset.bcProductCollections ? JSON.parse(nav.dataset.bcProductCollections) : [],
      excludedCollections: nav.dataset.bcExcludedCollections || '',
      collectionPriority: nav.dataset.bcCollectionPriority || 'first',
      collectionTitle: nav.dataset.bcCollectionTitle,
      collectionUrl: nav.dataset.bcCollectionUrl,
      pageTitle: nav.dataset.bcPageTitle,
      pageUrl: nav.dataset.bcPageUrl,
      articleTitle: nav.dataset.bcArticleTitle,
      articleUrl: nav.dataset.bcArticleUrl,
      blogTitle: nav.dataset.bcBlogTitle,
      blogUrl: nav.dataset.bcBlogUrl
    };

    // Show loading state
    this.showLoadingState();

    // Build and render breadcrumbs (async but don't block)
    this.buildTrail().then(trail => {
      this.hideLoadingState();
      this.renderBreadcrumbs(trail);
      this.renderJsonLd(trail);
      
      // Clean up noscript fallback
      const noscript = this.listEl.querySelector('noscript');
      if (noscript) noscript.remove();
    }).catch(error => {
      console.warn('Breadcrumb: Failed to build trail', error);
      this.hideLoadingState();
      // Show fallback breadcrumbs
      this.renderFallbackOnly();
    });
  }

  async buildTrail() {
    let trail = [];
    
    // For pages and articles, always use simple fallback trail (ignore stored navigation)
    if ((this.config.pageTitle && this.config.pageUrl) || (this.config.articleTitle && this.config.articleUrl)) {
      trail = await this.buildFallbackTrail();
    } else {
      // For products and collections, use stored trail
      trail = this.getStoredTrail();
      
      // Validate trail
      const isValidTrail = this.validateTrail(trail);
      
      if (!isValidTrail) {
        // Fallback trail for direct entry
        trail = await this.buildFallbackTrail();
      } else {
        // Ensure home is always first
        if (!trail.length || trail[0].url !== this.config.homeUrl) {
          trail.unshift({
            name: this.config.homeLabel,
            url: this.config.homeUrl
          });
        }
        
        // Add current page if not already in trail
        trail = this.addCurrentPage(trail);
      }
    }
    
    return trail;
  }

  getStoredTrail() {
    try {
      const stored = sessionStorage.getItem('bcTrail');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Filter out any products from stored trail (they shouldn't persist)
        return parsed.filter(node => !node.url.includes('/products/'));
      }
    } catch (e) {
      console.warn('Breadcrumb: Failed to read trail from sessionStorage', e);
    }
    return [];
  }

  validateTrail(trail) {
    if (!Array.isArray(trail) || trail.length === 0) return false;
    
    return trail.every(node => 
      node && 
      typeof node.name === 'string' && 
      typeof node.url === 'string' &&
      node.name.length > 0
    );
  }

  async buildFallbackTrail() {
    const trail = [
      { name: this.config.homeLabel, url: this.config.homeUrl }
    ];

    // For product pages, get collection from Liquid data
    if (this.config.productTitle && this.config.productUrl) {
      const productCollection = this.getBestProductCollection();

      if (productCollection) {
        trail.push(productCollection);
        trail.push({
          name: this.decodeHtmlEntities(this.config.productTitle),
          url: this.config.productUrl
        });
        return trail;
      }
    }
    
    // Try to use last visited collection as fallback, but only if it makes sense
    try {
      const lastCollection = sessionStorage.getItem('lastCollection');
      if (lastCollection && this.config.productTitle) {
        const collection = JSON.parse(lastCollection);
        if (collection && collection.name && collection.url) {
          // Check if the product title or URL suggests it belongs to this collection
          const productTitle = this.config.productTitle.toLowerCase();
          const collectionName = collection.name.toLowerCase();
          
          // Only use the collection if there's a logical connection
          const isRelevant = this.isProductRelevantToCollection(productTitle, collectionName, collection.url);
          
          if (isRelevant) {
            trail.push(collection);
            
            // Add product if on product page
            if (this.config.productTitle && this.config.productUrl) {
              trail.push({
                name: this.decodeHtmlEntities(this.config.productTitle),
                url: this.config.productUrl
              });
            }
            
            return trail;
          }
        }
      }
    } catch (e) {
      // Ignore and use default fallback
    }
    
    // Default fallback
    // For collection pages
    if (this.config.collectionTitle && this.config.collectionUrl) {
      trail.push({
        name: this.config.shopLabel,
        url: this.config.allUrl
      });
      trail.push({
        name: this.config.collectionTitle,
        url: this.config.collectionUrl
      });
    }
    // For product pages
    else if (this.config.productTitle && this.config.productUrl) {
      trail.push({
        name: this.config.allLabel,
        url: this.config.allUrl
      });
      trail.push({
        name: this.decodeHtmlEntities(this.config.productTitle),
        url: this.config.productUrl
      });
    }
    // For page templates
    else if (this.config.pageTitle && this.config.pageUrl) {
      trail.push({
        name: this.decodeHtmlEntities(this.config.pageTitle),
        url: this.config.pageUrl
      });
    }
    // For article templates (blog posts)
    else if (this.config.articleTitle && this.config.articleUrl) {
      if (this.config.blogTitle && this.config.blogUrl) {
        trail.push({
          name: this.decodeHtmlEntities(this.config.blogTitle),
          url: this.config.blogUrl
        });
      }
      trail.push({
        name: this.decodeHtmlEntities(this.config.articleTitle),
        url: this.config.articleUrl
      });
    }
    
    return trail;
  }

  addCurrentPage(trail) {
    const currentUrl = this.config.productUrl || this.config.collectionUrl || this.config.pageUrl || this.config.articleUrl;
    const currentTitle = this.config.productTitle || this.config.collectionTitle || this.config.pageTitle || this.config.articleTitle;
    
    if (!currentUrl || !currentTitle) return trail;
    
    // Remove any existing product pages from the trail (products shouldn't be in the middle)
    trail = trail.filter(node => !node.url.includes('/products/'));
    
    // Add current page as the last item (product, collection, or page)
    if (currentUrl) {
      const currentPath = currentUrl.replace(window.location.origin, '');
      
      // Check if current page is not already the last item
      const lastNode = trail[trail.length - 1];
      if (!lastNode || lastNode.url !== currentPath) {
        trail.push({
          name: this.decodeHtmlEntities(currentTitle),
          url: currentPath
        });
      }
    }
    
    return trail;
  }

  renderBreadcrumbs(trail) {
    if (!this.listEl) return;
    
    // Clear existing content (except noscript) - more efficient
    const items = this.listEl.querySelectorAll('.breadcrumb__item:not(noscript *)');
    items.forEach(item => item.remove());
    
    // Use DocumentFragment for batch DOM operations
    const fragment = document.createDocumentFragment();
    
    trail.forEach((node, index) => {
      const li = document.createElement('li');
      li.className = 'breadcrumb__item';
      
      // Add separator for all items except first
      if (index > 0) {
        const separator = document.createElement('span');
        separator.className = 'breadcrumb__separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = this.separator;
        li.appendChild(separator);
      }
      
      // Last item is current page (no link)
      if (index === trail.length - 1) {
        const span = document.createElement('span');
        span.className = 'breadcrumb__text';
        span.setAttribute('aria-current', 'page');
        span.textContent = this.truncateText(this.decodeHtmlEntities(node.name), 50);
        li.appendChild(span);
      } else {
        const link = document.createElement('a');
        link.href = node.url;
        link.className = 'breadcrumb__link link-underline link-underline--opacity';
        
        // Special handling for home link (first item)
        if (index === 0 && node.url === this.config.homeUrl) {
          const homeIcon = document.createElement('span');
          homeIcon.innerHTML = `<svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" class="breadcrumb__home-icon">
            <path d="M9.293 2.293a1 1 0 0 1 1.414 0l7 7A1 1 0 0 1 17 11h-1v6a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-3a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-6H3a1 1 0 0 1-.707-1.707l7-7Z" clip-rule="evenodd" fill-rule="evenodd"></path>
          </svg>`;
          
          const srOnly = document.createElement('span');
          srOnly.className = 'sr-only';
          srOnly.textContent = this.decodeHtmlEntities(node.name);
          
          link.appendChild(homeIcon);
          link.appendChild(srOnly);
        } else {
          link.textContent = this.decodeHtmlEntities(node.name);
        }
        
        li.appendChild(link);
      }
      
      fragment.appendChild(li);
    });
    
    // Single DOM write operation
    this.listEl.appendChild(fragment);
  }

  renderJsonLd(trail) {
    // Render JSON-LD on all pages where breadcrumbs are shown
    // Google requires structured data to match visible breadcrumbs

    try {
      // Remove any existing breadcrumb JSON-LD first
      const existing = document.querySelector('script[data-breadcrumb]');
      if (existing) existing.remove();

      // Create simplified JSON-LD
      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": trail.map((node, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": this.decodeHtmlEntities(node.name),
          "item": location.origin + node.url
        }))
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-breadcrumb', '');
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    } catch (e) {
      // Silently fail for better performance
    }
  }

  isProductRelevantToCollection(productTitle, collectionName, collectionUrl) {
    // List of keywords that suggest relevance
    const relevanceRules = [
      // Socken/Socks
      {
        collection: ['socken', 'socks'],
        product: ['socken', 'socks']
      },
      // Business
      {
        collection: ['business'],
        product: ['business', 'formal', 'office', 'work']
      },
      // Tennis
      {
        collection: ['tennis'],
        product: ['tennis', 'sport', 'athletic']
      },
      // Caps/Hats
      {
        collection: ['cap', 'hat', 'mütze'],
        product: ['cap', 'hat', 'mütze', 'beanie']
      },
      // Fashion/Clothing
      {
        collection: ['fashion', 'kleidung', 'apparel'],
        product: ['shirt', 'hoodie', 'jacket', 'pants', 'dress']
      }
    ];

    // Check if collection and product share relevant keywords
    for (const rule of relevanceRules) {
      const collectionMatch = rule.collection.some(keyword => 
        collectionName.includes(keyword)
      );
      const productMatch = rule.product.some(keyword => 
        productTitle.includes(keyword)
      );
      
      if (collectionMatch && productMatch) {
        return true;
      }
    }

    // Additional check: if collection URL suggests product type
    if (collectionUrl.includes('/collections/') && productTitle.includes('-')) {
      const urlParts = collectionUrl.split('/collections/')[1];
      if (urlParts) {
        const collectionHandle = urlParts.split('/')[0];
        // Check if product title contains parts of the collection handle
        const handleParts = collectionHandle.split('-');
        const titleWords = productTitle.split(/[\s-]+/);
        
        const commonWords = handleParts.filter(part => 
          titleWords.some(word => word.includes(part) || part.includes(word))
        );
        
        // If at least 1 word matches, consider it relevant
        if (commonWords.length >= 1 && commonWords[0].length > 2) {
          return true;
        }
      }
    }

    return false;
  }

  getBestProductCollection() {
    // Get collections from Liquid data (no API call needed)
    if (!this.config.productCollections || this.config.productCollections.length === 0) {
      return null;
    }

    // Get excluded collections list (handles from collection_list setting)
    const excludedHandles = this.config.excludedCollections
      .split(',')
      .map(h => h.trim().toLowerCase())
      .filter(h => h.length > 0);

    // Filter out excluded and default collections
    const validCollections = this.config.productCollections.filter(collection => {
      const handle = collection.handle.toLowerCase();
      const title = collection.title.toLowerCase();

      // Skip default collections
      if (handle === 'all' || handle === 'frontpage' || handle.includes('hidden') ||
          handle === 'alle-produkte' || title === 'all products' || title === 'alle produkte') {
        return false;
      }

      // Skip excluded collections (by handle)
      if (excludedHandles.includes(handle)) {
        return false;
      }

      return true;
    });

    if (validCollections.length === 0) {
      return null;
    }

    // Select collection based on priority setting
    let selectedCollection;

    switch (this.config.collectionPriority) {
      case 'largest':
        // Choose collection with most products
        selectedCollection = validCollections.reduce((prev, curr) =>
          (curr.products_count > prev.products_count) ? curr : prev
        );
        break;

      case 'specific':
        // Choose collection with fewest products (most specific)
        selectedCollection = validCollections.reduce((prev, curr) =>
          (curr.products_count < prev.products_count) ? curr : prev
        );
        break;

      case 'first':
      default:
        // Choose first collection alphabetically
        selectedCollection = validCollections.sort((a, b) =>
          a.title.localeCompare(b.title)
        )[0];
        break;
    }

    if (selectedCollection) {
      return {
        name: this.decodeHtmlEntities(selectedCollection.title),
        url: selectedCollection.url
      };
    }

    return null;
  }



  getCachedData(key) {
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        const { data, timestamp, ttl } = JSON.parse(cached);
        if (Date.now() - timestamp < ttl) {
          return data;
        }
        sessionStorage.removeItem(key);
      }
    } catch (e) {
      // Ignore cache errors
    }
    return null;
  }

  setCachedData(key, data, ttl) {
    try {
      sessionStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now(),
        ttl
      }));
    } catch (e) {
      // Ignore cache errors
    }
  }

  decodeHtmlEntities(text) {
    if (!text) return text;
    
    // Create a temporary element to decode HTML entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    return tempDiv.textContent || tempDiv.innerText || text;
  }

  showLoadingState() {
    if (!this.listEl) return;
    
    this.listEl.innerHTML = `
      <li class="breadcrumb__item breadcrumb__loading">
        <span class="breadcrumb__text" style="opacity: 0.6;">
          ${this.config.homeLabel} › ${this.config.allLabel} › ...
        </span>
      </li>
    `;
  }

  hideLoadingState() {
    if (!this.listEl) return;
    
    const loadingItem = this.listEl.querySelector('.breadcrumb__loading');
    if (loadingItem) {
      loadingItem.remove();
    }
  }

  renderFallbackOnly() {
    const trail = [
      { name: this.config.homeLabel, url: this.config.homeUrl }
    ];
    
    if (this.config.productTitle) {
      trail.push({ name: this.config.allLabel, url: this.config.allUrl });
      trail.push({ name: this.config.productTitle, url: this.config.productUrl });
    } else if (this.config.collectionTitle) {
      trail.push({ name: this.config.collectionTitle, url: this.config.collectionUrl });
    }
    
    this.renderBreadcrumbs(trail);
    this.renderJsonLd(trail);
  }

  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }
}

// Initialize breadcrumb manager
new BreadcrumbManager();

/**
 * Breadcrumb Trail Tracker
 * Saves navigation context when clicking on product/collection links
 */
class BreadcrumbTracker {
  constructor() {
    this.init();
  }

  init() {
    // Use mousedown for better timing with passive listeners
    document.addEventListener('mousedown', (e) => {
      const link = e.target.closest('a[href*="/products/"], a[href*="/collections/"]');
      if (!link) return;
      
      this.saveCurrentContext(link);
    }, { passive: true, capture: true });

    // Track menu clicks with explicit breadcrumb paths
    document.addEventListener('mousedown', (e) => {
      const link = e.target.closest('a[data-bc-path]');
      if (!link) return;
      
      this.saveExplicitPath(link);
    }, { passive: true, capture: true });
  }

  saveCurrentContext(link) {
    // If we're on a product page going to a collection, clear the trail
    if (window.location.pathname.includes('/products/') && 
        link.href.includes('/collections/')) {
      try {
        // Clear the trail when going back to collections from products
        sessionStorage.removeItem('bcTrail');
      } catch (e) {
        console.warn('Breadcrumb: Failed to clear trail', e);
      }
      return;
    }
    
    // If we're going from product to product (via recommendations), clear trail
    // This prevents unrelated products from showing wrong collection paths
    if (window.location.pathname.includes('/products/') && 
        link.href.includes('/products/')) {
      try {
        // Clear trail for product-to-product navigation
        // Let the new product page determine its own appropriate breadcrumb
        sessionStorage.removeItem('bcTrail');
        sessionStorage.removeItem('lastCollection');
      } catch (e) {
        console.warn('Breadcrumb: Failed to clear trail', e);
      }
      return;
    }

    try {
      const trail = this.buildCurrentTrail();
      if (trail.length > 0) {
        sessionStorage.setItem('bcTrail', JSON.stringify(trail));
      }
    } catch (e) {
      console.warn('Breadcrumb: Failed to save trail', e);
    }
  }

  saveExplicitPath(link) {
    try {
      const pathData = link.dataset.bcPath;
      if (pathData) {
        const trail = JSON.parse(pathData);
        if (Array.isArray(trail) && trail.length > 0) {
          sessionStorage.setItem('bcTrail', JSON.stringify(trail));
        }
      }
    } catch (e) {
      console.warn('Breadcrumb: Failed to save explicit path', e);
    }
  }

  buildCurrentTrail() {
    const trail = [];
    
    // Always start with home
    trail.push({
      name: this.getLocalizedLabel('home', 'Home'),
      url: '/'
    });
    
    // Check if we're on a collection page
    const collectionMatch = window.location.pathname.match(/\/collections\/([^\/]+)/);
    if (collectionMatch) {
      // Try to get collection title from page
      const collectionTitle = this.getPageTitle() || this.formatHandle(collectionMatch[1]);
      trail.push({
        name: collectionTitle,
        url: `/collections/${collectionMatch[1]}`
      });
    }
    
    // Check for existing breadcrumb UI on page
    const existingBreadcrumbs = this.parseExistingBreadcrumbs();
    if (existingBreadcrumbs.length > trail.length) {
      return existingBreadcrumbs;
    }
    
    return trail;
  }

  parseExistingBreadcrumbs() {
    const trail = [];
    const breadcrumbNav = document.querySelector('[aria-label*="Breadcrumb" i] ol, .breadcrumb__list');
    
    if (breadcrumbNav) {
      breadcrumbNav.querySelectorAll('a, span[aria-current="page"]').forEach(el => {
        const name = el.textContent.trim();
        const url = el.getAttribute('href') || window.location.pathname;
        
        // Skip separators and empty items
        if (name && !['›', '/', '>', '»'].includes(name)) {
          trail.push({ name, url });
        }
      });
    }
    
    return trail;
  }

  getPageTitle() {
    // Try various selectors for page title
    const selectors = [
      'h1.collection__title',
      'h1.section-header__title',
      '.collection-hero__title',
      'h1'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        return element.textContent.trim();
      }
    }
    
    return null;
  }

  formatHandle(handle) {
    // Convert URL handle to readable format
    return handle
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getLocalizedLabel(key, fallback) {
    // Try to get localized label from Shopify theme translations
    if (window.theme?.strings?.[key]) {
      return window.theme.strings[key];
    }
    return fallback;
  }
}

// Initialize tracker
new BreadcrumbTracker();